import './BrandInsights.css';
import { useState, useRef, useEffect } from 'react';
import { HiOutlineOfficeBuilding, HiOutlineUserGroup, HiOutlineCalendar, HiOutlineShoppingCart, HiOutlineCurrencyDollar, HiOutlineChevronDown } from 'react-icons/hi';
import { fetchBrandData } from '../../services/brandSitesService';
import { fetchBrandsByUltimateParent } from '../../services/brandService';
import { getTooltipText } from '../../utils/tooltip';
import Tooltip from '../Tooltip/Tooltip';
import Spinner from '../Spinner/Spinner';

interface BrandInsightsProps {
  brandName?: string;
  isUltimateParent?: boolean;
}

function BrandInsights({ brandName, isUltimateParent }: BrandInsightsProps) {
  const [selectedSolution, setSelectedSolution] = useState('All Solutions');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [brandData, setBrandData] = useState({
    isTopRetailer: false,
    name: 'Unknown Brand',
    totalSites: 0,
    acquiredSites: 0,
    estimatedCartsPerStore: 0,
    highCrimeStores: 0,
    sitesInHighGkap: 0,
    solutions: {
      purchek: 0,
      cartContainment: 0,
      cartManager: 0,
      subscription: 0,
      faceFirst: 0
    },
    crimeDistribution: {
      critical: { count: 0, label: 'Critical', percentage: 0 },
      high: { count: 0, label: 'High', percentage: 0 },
      medium: { count: 0, label: 'Medium', percentage: 0 },
      low: { count: 0, label: 'Low', percentage: 0 },
      noData: { count: 0, label: 'No Data', percentage: 0 }
    },
    gkapDistribution: {
      below20: { count: 0, label: 'GKAP Below 20%', percentage: 0 },
      range20to40: { count: 0, label: 'GKAP 20-40%', percentage: 0 },
      range40to60: { count: 0, label: 'GKAP 40-60%', percentage: 0 },
      range60to80: { count: 0, label: 'GKAP 60-80%', percentage: 0 },
      above80: { count: 0, label: 'GKAP 80%+', percentage: 0 }
    },
    inactiveWheelsData: [] as any[],
    migratedWheelsData: [] as any[]
  });

  useEffect(() => {
    const loadBrandData = async () => {
      if (!brandName) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const formattedName = decodeURIComponent(brandName).toUpperCase();

        let brandNamesToFetch: string[];

        if (isUltimateParent) {
          // Fetch all brand names under this ultimate parent
          const brandsList = await fetchBrandsByUltimateParent(formattedName);
          brandNamesToFetch = brandsList.map((b: any) => b.name);
        } else {
          brandNamesToFetch = [formattedName];
        }

        // Single query for all brands
        const { stats: data } = await fetchBrandData(brandNamesToFetch);

        const defaultCrime = {
          critical: { count: 0, label: 'Critical', percentage: 0 },
          high: { count: 0, label: 'High', percentage: 0 },
          medium: { count: 0, label: 'Medium', percentage: 0 },
          low: { count: 0, label: 'Low', percentage: 0 },
          noData: { count: 0, label: 'No Data', percentage: 0 }
        };
        const defaultGkap = {
          below20: { count: 0, label: 'GKAP Below 20%', percentage: 0 },
          range20to40: { count: 0, label: 'GKAP 20-40%', percentage: 0 },
          range40to60: { count: 0, label: 'GKAP 40-60%', percentage: 0 },
          range60to80: { count: 0, label: 'GKAP 60-80%', percentage: 0 },
          above80: { count: 0, label: 'GKAP 80%+', percentage: 0 }
        };

        setBrandData((prev) => ({
          ...prev,
          name: isUltimateParent ? formattedName : data.name,
          totalSites: data.totalSites,
          acquiredSites: data.acquiredSites,
          estimatedCartsPerStore: data.estimatedCartsPerStore || 0,
          highCrimeStores: data.highCrimeStores || 0,
          sitesInHighGkap: data.sitesInHighGkap || 0,
          solutions: data.solutions,
          crimeDistribution: data.crimeDistribution || defaultCrime,
          gkapDistribution: data.gkapDistribution || defaultGkap,
          inactiveWheelsData: data.inactiveWheelsData || [],
          migratedWheelsData: data.migratedWheelsData || []
        }));
      } catch (error) {
        console.error('Failed to load brand insights:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBrandData();
  }, [brandName, isUltimateParent]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const statsCards = [
    {
      icon: <HiOutlineOfficeBuilding className="stat-icon blue" />,
      label: 'Total Sites',
      value: brandData.totalSites.toLocaleString(),
      change: '+5.2%',
      trend: 'up',
      tooltip: getTooltipText('serviceableSites')
    },
    {
      icon: <HiOutlineUserGroup className="stat-icon green" />,
      label: 'Acquired Sites',
      value: brandData.acquiredSites.toLocaleString(),
      change: '+7.3%',
      trend: 'up',
      tooltip: getTooltipText('acquiredSite')
    },
    {
      icon: <HiOutlineCalendar className="stat-icon orange" />,
      label: 'Service Due',
      value: '-',
      change: '-15.2%',
      trend: 'down',
      tooltip: ''
    },
    {
      icon: <HiOutlineShoppingCart className="stat-icon purple" />,
      label: 'Number of Carts',
      value: brandData.estimatedCartsPerStore.toLocaleString(),
      change: '+4.8%',
      trend: 'up',
      tooltip: ''
    },
    {
      icon: <HiOutlineCurrencyDollar className="stat-icon teal" />,
      label: 'Revenue Potential',
      value: '$0.0M',
      change: '+8.7%',
      trend: 'up',
      tooltip: ''
    }
  ];

  const solutionOptions = [
    'All Solutions',
    'Purchek',
    'Cart Containment',
    'Cart Manager',
    'Subscription',
    'Paired Wheel'
  ];

  const calculatePercentage = (value: number, total: number): string => {
    if (total === 0) return '0.0%';
    return ((value / total) * 100).toFixed(1) + '%';
  };

  const solutionDetails = {
    'Purchek': {
      title: 'Purchek Deployment',
      value: brandData.solutions.purchek,
      percentage: `${calculatePercentage(brandData.solutions.purchek, brandData.totalSites)} of total sites`,
      bgColor: '#eff6ff'
    },
    'Cart Containment': {
      title: 'Cart Containment Deployment',
      value: brandData.solutions.cartContainment,
      percentage: `${calculatePercentage(brandData.solutions.cartContainment, brandData.totalSites)} of total sites`,
      bgColor: '#fef3e2'
    },
    'Cart Manager': {
      title: 'Cart Manager Deployment',
      value: brandData.solutions.cartManager,
      percentage: `${calculatePercentage(brandData.solutions.cartManager, brandData.totalSites)} of total sites`,
      bgColor: '#f0fdf4'
    },
    'Subscription': {
      title: 'Subscription Deployment',
      value: brandData.solutions.subscription,
      percentage: `${calculatePercentage(brandData.solutions.subscription, brandData.totalSites)} of total sites`,
      bgColor: '#faf5ff'
    },
    'Paired Wheel': {
      title: 'Paired Wheel Deployment',
      value: brandData.solutions.faceFirst,
      percentage: `${calculatePercentage(brandData.solutions.faceFirst, brandData.totalSites)} of total sites`,
      bgColor: '#fef2f2'
    }
  };

  const allSolutionsData = [
    { label: 'Purchek Sites', value: brandData.solutions.purchek },
    { label: 'Cart Containment Sites', value: brandData.solutions.cartContainment },
    { label: 'Cart Manager Sites', value: brandData.solutions.cartManager },
    { label: 'Subscription Sites', value: brandData.solutions.subscription },
    { label: 'Paired Wheel Sites', value: brandData.solutions.faceFirst }
  ];

  if (loading) {
    return (
      <div className="brand-insights" style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner size="small" />
      </div>
    );
  }

  return (
    <div className="brand-insights">
      {/* Stats Cards */}
      <div className="stats-grid">
        {statsCards.map((stat, index) => (
          <div key={index} className="stat-card" title={stat.tooltip}>
            <div className="stat-header">
              {stat.icon}
              <span className="stat-label">{stat.label}</span>
            </div>
            <div className="stat-value">{stat.value}</div>
            {/* <div className={`stat-change ${stat.trend}`}>
              <span className="trend-icon">{stat.trend === 'up' ? '↗' : '↘'}</span>
              <span className="trend-text">{stat.change}</span>
              <span className="trend-label">from last period</span>
            </div> */}
          </div>
        ))}
      </div>

      {/* Key Insights */}
      <div className="brand-key-insights-section">
        <div className="brand-key-insights-header">
          <h3>Key Insights</h3>
          <div className="brand-insights-controls">
            <div className="insights-dropdown-wrapper" ref={dropdownRef}>
              <button
                className="insights-dropdown"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {selectedSolution}
                <HiOutlineChevronDown className={`dropdown-icon ${dropdownOpen ? 'open' : ''}`} />
              </button>
              {dropdownOpen && (
                <div className="insights-dropdown-menu">
                  {solutionOptions.map((option) => {
                    let tooltipKey = '';
                    if (option === 'Purchek') tooltipKey = 'purchek';
                    else if (option === 'Cart Containment') tooltipKey = 'cartControl';
                    else if (option === 'Cart Manager') tooltipKey = 'cartManager';
                    else if (option === 'Subscription') tooltipKey = 'subscription';
                    else if (option === 'Paired Wheel') tooltipKey = 'faceFirst';

                    return (
                      <button
                        key={option}
                        className={`insights-dropdown-item ${selectedSolution === option ? 'active' : ''}`}
                        onClick={() => {
                          setSelectedSolution(option);
                          setDropdownOpen(false);
                        }}
                        title={tooltipKey ? getTooltipText(tooltipKey as any) : ''}
                      >
                        {option}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button className="configure-btn">
              <svg className="configure-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              Configure
            </button>
          </div>
        </div>

        {selectedSolution === 'All Solutions' ? (
          <div className="insights-grid">
            {allSolutionsData.map((insight, index) => {
              let tooltipKey = '';
              if (insight.label === 'Purchek Sites') tooltipKey = 'purchek';
              else if (insight.label === 'Cart Containment Sites') tooltipKey = 'cartControl';
              else if (insight.label === 'Cart Manager Sites') tooltipKey = 'cartManager';
              else if (insight.label === 'Subscription Sites') tooltipKey = 'subscription';
              else if (insight.label === 'Paired Wheel Sites') tooltipKey = 'faceFirst';

              return (
                <div key={index} className="insight-card" title={tooltipKey ? getTooltipText(tooltipKey as any) : ''}>
                  <div className="insight-label">{insight.label}</div>
                  <div className="insight-value">{insight.value}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="single-insight-card" style={{ backgroundColor: solutionDetails[selectedSolution as keyof typeof solutionDetails].bgColor }}>
            <Tooltip
              content={getTooltipText(
                (
                  selectedSolution === 'Purchek' ? 'purchek' :
                  selectedSolution === 'Cart Containment' ? 'cartControl' :
                  selectedSolution === 'Cart Manager' ? 'cartManager' :
                  selectedSolution === 'Subscription' ? 'subscription' :
                  selectedSolution === 'Paired Wheel' ? 'faceFirst' :
                  'purchek'
                ) as any
              )}
              side="top"
            >
              <div className="single-insight-title">{solutionDetails[selectedSolution as keyof typeof solutionDetails].title}</div>
            </Tooltip>
            <div className="single-insight-value">{solutionDetails[selectedSolution as keyof typeof solutionDetails].value} Sites</div>
            <div className="single-insight-percentage">{solutionDetails[selectedSolution as keyof typeof solutionDetails].percentage}</div>
          </div>
        )}
      </div>

      {/* Crime and GKAP Cards */}
      <div className="crime-gkap-grid">
        {/* High Crime Areas Card */}
        <div className="crime-gkap-card">
          <h3 className="card-title" title={getTooltipText('crimeIndex')}>Crime Analysis</h3>
          <div className="card-content">
            <div className="crime-distribution">
              {[
                { key: 'critical', color: '#dc2626' },
                { key: 'high', color: '#f97316' },
                { key: 'medium', color: '#eab308' },
                { key: 'low', color: '#84cc16' },
                { key: 'noData', color: '#9ca3af' }
              ].map(({ key, color }) => {
                const cat = brandData.crimeDistribution[key as keyof typeof brandData.crimeDistribution];
                return (
                  <div key={key} className="crime-category">
                    <div className="crime-category-header">
                      <span className="crime-label">{cat.label}</span>
                      <span className="crime-stats">{cat.count} sites ({cat.percentage}%)</span>
                    </div>
                    <div className="crime-bar-background">
                      <div
                        className="crime-bar-fill"
                        style={{
                          width: `${Math.max(cat.percentage, 5)}%`,
                          backgroundColor: color
                        }}
                      >
                        {cat.count > 0 && cat.percentage > 10 && <span>{cat.count}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="crime-total-row">
              <span className="crime-total-label">Total Sites Analyzed</span>
              <span className="crime-total-value">{brandData.totalSites}</span>
            </div>
          </div>
        </div>

        {/* GKAP Distribution Card */}
        <div className="crime-gkap-card">
          <h3 className="card-title" title={getTooltipText('gkap')}>Distribution across USA by GKAP percentage</h3>
          <div className="card-content">
            <div className="crime-distribution">
              {[
                { key: 'below20', color: '#22c55e' },
                { key: 'range20to40', color: '#84cc16' },
                { key: 'range40to60', color: '#eab308' },
                { key: 'range60to80', color: '#f97316' },
                { key: 'above80', color: '#dc2626' }
              ].map(({ key, color }) => {
                const cat = brandData.gkapDistribution[key as keyof typeof brandData.gkapDistribution];
                return (
                  <div key={key} className="crime-category">
                    <div className="crime-category-header">
                      <span className="crime-label">{cat.label}</span>
                      <span className="crime-stats">{cat.count} sites ({cat.percentage}%)</span>
                    </div>
                    <div className="crime-bar-background">
                      <div
                        className="crime-bar-fill"
                        style={{
                          width: `${Math.max(cat.percentage, 5)}%`,
                          backgroundColor: color
                        }}
                      >
                        {cat.count > 0 && cat.percentage > 10 && <span>{cat.count}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="crime-total-row">
              <span className="crime-total-label">Total Sites Analyzed</span>
              <span className="crime-total-value">{brandData.totalSites}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Sites with Most Inactive Wheels Table */}
      {/* <div className="sites-table-section">
        <div className="table-header">
          <h3>Top Sites with Most Inactive Wheels</h3>
          <button className="view-all-btn" onClick={() => setShowAllInactive(!showAllInactive)}>
            {showAllInactive ? 'Show Less' : 'View All'}
          </button>
        </div>
        <div className="table-container">
          <table className="sites-table">
            <thead>
              <tr>
                <th>Store Name</th>
                <th>Location</th>
                <th>Inactive Wheels</th>
                <th>Total Carts</th>
              </tr>
            </thead>
            <tbody>
              {(showAllInactive ? brandData.inactiveWheelsData : brandData.inactiveWheelsData.slice(0, 5)).map((row: any, index: number) => (
                <tr key={index}>
                  <td>{row.storeName}</td>
                  <td>{row.location}</td>
                  <td className="highlight-red">{row.inactiveWheels}</td>
                  <td>{row.totalCarts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* Top Sites with Most Migrated Wheels Table */}
      {/* <div className="sites-table-section">
        <div className="table-header">
          <h3>Top Sites with Most Migrated Wheels</h3>
          <button className="view-all-btn" onClick={() => setShowAllMigrated(!showAllMigrated)}>
            {showAllMigrated ? 'Show Less' : 'View All'}
          </button>
        </div>
        <div className="table-container">
          <table className="sites-table">
            <thead>
              <tr>
                <th>Store Name</th>
                <th>Location</th>
                <th>Migrated Wheels</th>
                <th>Total Carts</th>
              </tr>
            </thead>
            <tbody>
              {(showAllMigrated ? brandData.migratedWheelsData : brandData.migratedWheelsData.slice(0, 5)).map((row: any, index: number) => (
                <tr key={index}>
                  <td>{row.storeName}</td>
                  <td>{row.location}</td>
                  <td className="highlight-orange">{row.migratedWheels}</td>
                  <td>{row.totalCarts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}
    </div>
  );
}

export default BrandInsights;